import { ChangeDetectionStrategy, Component } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormGroup } from '@angular/forms';
import { LocalStorageService } from '@cpak/main-features-lib/services';
import { UserModel } from '@cpak/adp-cpak-users-service-models';
import {
  UserRouteBox,
  UserRouteInboxOutboxSortOptions,
} from '@cpak/adp-cpak-quotes-service-models/dist/business/routes/get-user-inbox-outbox';
import { SortOrder, StartResponsePayload } from '@cpak/adp-cpak-bff-service-models';
import { ButtonNamesGA } from '@cpak/consts';
import { ActivatedRoute, Router } from '@angular/router';
import { StartService } from '@cpak/main-features-lib/services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import * as CryptoJS from 'crypto-js';
import { getBrowserName, getDeviceType } from '@cpak/shared/utils';
import { CreateUserActivityRequestPayload } from '@cpak/adp-cpak-users-service-models';
import { UserApiService } from '@cpak/main-features-lib/services';
import { catchError, EMPTY } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'cpak-chat-panel',
  templateUrl: './chat-panel.component.html',
  styleUrls: ['./chat-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatPanelComponent {
  public users!: UserModel[];

  public selectedPakTab = UserRouteBox.inbox;

  private readonly RoutingPakTab: typeof UserRouteBox = UserRouteBox;

  public form!: FormGroup;
  public countryId = '';

  public isTableLoading!: boolean;
  public isContentLoading!: boolean;

  private readonly defaultSorting = {
    sortField: UserRouteInboxOutboxSortOptions.lastUpdate,
    sortOrder: SortOrder.asc,
  };
  private readonly stateRoutingPakKeyLabel = 'routingPaksUsersKey';
  protected sortingState?: {
    sortField: UserRouteInboxOutboxSortOptions;
    sortOrder: number;
  } = this.localStorage.getItem(this.stateRoutingPakKeyLabel);
  public stateRoutingPaksUsersKey = '';
  private initSort = this.sortingState?.sortField || this.defaultSorting.sortField;
  private initSortOrder = (this.sortingState?.sortOrder === 1 ? SortOrder.asc : SortOrder.desc) || SortOrder.asc;

  public sort = this.initSort;
  public sortOrder = this.initSortOrder;

  public readonly ButtonNamesGA = ButtonNamesGA;
  public secretKey = 'Fe9EmuKPbOtmviFKCqBJpAPXx7v5Zmsr'; // Must be 32 chars for AES-256
  public userEmail = '';
  public userId = '';
  public plant = '';
  public frameUrl = '';
  public iframeUrl!: SafeResourceUrl;

  constructor(
    private dialogRef: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private localStorage: LocalStorageService,
    private router: Router,
    private route: ActivatedRoute,
    private startService: StartService,
    sanitizer: DomSanitizer,
    private userApiService: UserApiService
  ) {
    this.startService.getStartResponse().subscribe((response: StartResponsePayload | null) => {
      if (response) {
        const { userInfo } = response.data;
        this.userId = userInfo.user.id;
        this.userEmail = userInfo.user.email;
        this.plant = response.data.config.defaultPlant;

        const chatRequest: ChatRequest = {
          userId: this.userId,
          userEmail: this.userEmail,
          plant: this.plant,
        };

        let encryptedString = this.encryptData(chatRequest);

        let basicUrl =
          'https://basicpythonapp-fjeyfed0daaehtcr.northcentralus-01.azurewebsites.net/?key=' + encryptedString;
        this.iframeUrl = sanitizer.bypassSecurityTrustResourceUrl(basicUrl);

        this.addUserActivity();
      }
    });
  }

  public closeModal(): void {
    this.dialogRef.close();
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  public encryptData(data: ChatRequest): string {
    const jsonData = JSON.stringify(data);
    const encrypted = CryptoJS.AES.encrypt(jsonData, CryptoJS.enc.Utf8.parse(this.secretKey), {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    }).toString();
    return encodeURIComponent(encrypted); // Encode for URL safety
  }

  private addUserActivity(): void {
    if (!this.userId) {
      return;
    }

    const userAgent = navigator.userAgent;
    const userActivityPayload: CreateUserActivityRequestPayload = {
      data: {
        id: this.userId,
        type: 'Chatbot',
        userId: this.userId,
        date: Date.now(),
        browser: getBrowserName(userAgent),
        device: getDeviceType(userAgent),
        ipAddress: '',
        userAgent,
      },
    };

    this.userApiService
      .createUserActivity(userActivityPayload)
      .pipe(
        untilDestroyed(this),
        catchError(error => {
          console.log('Failed to log user activity:', error);
          return EMPTY;
        })
      )
      .subscribe();
  }
}
export type ChatRequest = {
  userId: string;
  userEmail: string;
  plant: string;
};
